/**
 * Consent Banner Component
 *
 * Displays GDPR consent banner for anonymous users
 * Persists consent choice to localStorage
 * Respects user preferences across sessions
 *
 * User Story: US2 - Anonymous User Consent Management
 * Feature: Analytics consent before tracking
 */

import React, { useState, useEffect } from 'react';
import { CODEMERLIN_ANALYTICS_CONSENT_CHANGED } from '@/lib/analytics-consent-events';

/**
 * Consent record structure
 */
interface ConsentRecord {
  consentGiven: boolean;
  timestamp: string;
  version: string;
  fingerprint?: string;
}

/**
 * Props for ConsentBanner
 */
interface ConsentBannerProps {
  onConsentChange?: (consentGiven: boolean) => void;
}

/**
 * ConsentBanner Component
 *
 * Shows GDPR consent banner at bottom of page
 * Allows users to accept or decline analytics tracking
 * Stores choice in localStorage with timestamp and version
 */
export function ConsentBanner(props: ConsentBannerProps): React.ReactElement | null {
  const [isVisible, setIsVisible] = useState(false);

  const CONSENT_KEY = 'codemerlin_analytics_consent';
  const CONSENT_VERSION = '2024-12-21';

  // Check localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);

    if (stored) {
      try {
        JSON.parse(stored);
        // Banner is hidden if we already have consent stored
        setIsVisible(false);
      } catch {
        // Invalid stored consent, show banner
        setIsVisible(true);
      }
    } else {
      // No consent stored, show banner
      setIsVisible(true);
    }
  }, []);

  /**
   * Handle consent acceptance
   */
  const handleAccept = (): void => {
    const consentRecord: ConsentRecord = {
      consentGiven: true,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION,
    };

    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentRecord));
    setIsVisible(false);
    window.dispatchEvent(new CustomEvent(CODEMERLIN_ANALYTICS_CONSENT_CHANGED));

    if (props.onConsentChange) {
      props.onConsentChange(true);
    }
  };

  /**
   * Handle consent rejection
   */
  const handleReject = (): void => {
    const consentRecord: ConsentRecord = {
      consentGiven: false,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION,
    };

    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentRecord));
    setIsVisible(false);
    window.dispatchEvent(new CustomEvent(CODEMERLIN_ANALYTICS_CONSENT_CHANGED));

    if (props.onConsentChange) {
      props.onConsentChange(false);
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-slate-900 text-white p-4 border-t border-slate-700 z-50">
      <div className="container mx-auto max-w-6xl">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex-1">
            <p className="text-sm leading-relaxed">
              We use analytics to understand how you use our platform and improve your experience.
              We respect your privacy and only collect anonymous usage data.{' '}
              <a
                href="/privacy"
                className="underline hover:text-slate-300"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </a>
            </p>
          </div>

          <div className="flex gap-3 md:flex-shrink-0">
            <button
              onClick={handleReject}
              className="px-4 py-2 rounded border border-slate-500 text-white hover:bg-slate-800 transition-colors text-sm font-medium"
              type="button"
            >
              Decline
            </button>
            <button
              onClick={handleAccept}
              className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors text-sm font-medium"
              type="button"
            >
              Accept
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Get stored consent record from localStorage
 *
 * @returns Consent record or null if not found/invalid
 */
export function getStoredConsent(): ConsentRecord | null {
  try {
    const stored = localStorage.getItem('codemerlin_analytics_consent');
    if (!stored) {
      return null;
    }
    return JSON.parse(stored) as ConsentRecord;
  } catch {
    return null;
  }
}

/**
 * Check if user has given consent
 *
 * @returns true if consent given, false if declined, null if not set
 */
export function hasConsent(): boolean | null {
  const record = getStoredConsent();
  return record?.consentGiven ?? null;
}

/**
 * Clear stored consent (for testing/debugging)
 */
export function clearConsent(): void {
  localStorage.removeItem('codemerlin_analytics_consent');
}

export default ConsentBanner;
